import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alignment, ButtonTypes, ChatInputButton } from 'pages/chat/chat';
import { FileUploadPopover } from './fileUploaderPopover';
import { FileStack, Loader2, PlusIcon, Send, X } from 'lucide-react';
import {
  uploadFilesToVectorStorage,
  uploadFileToLocalStorage,
} from '../../../services/storage-service';
import { Attachment, ButtonIds, MessageRole, VectorUploadItem, Mention } from '../types/chat';
import { v4 as uuidv4 } from 'uuid';
import { useModelStore } from '../../../state/store/chat/use-model-store';
import { Button } from 'components/ui/button';
import { useToast } from '../../../hooks/use-toast';
import { allSupportedImageExtensions, modelSupportedExtensionsMap, VECTORIZE_EXCLUDE_EXTENSIONS } from '../types/model.types';
import { useChatStore } from '../../../state/store/chat/use-chat-store';
import { useTranslation } from 'react-i18next';
import { useConfig } from 'hooks/use-config';
import CustomToast from 'components/core/CustomToast';
import { useNotificationStore } from '../../../state/store/common/useNotificationStore';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { useTheme } from '../../../components/core/theme-provider';
// import { useNotification } from 'hooks/use-notification';
// import { useCollaborationContext } from '../../../state/collaboration-websocket-middleware';
import { DictateButton } from './DictateButton';
import { TypingIndicator } from './TypingIndicator';
import { formatDate } from '../../../utils/custom-date';
import { ActiveToolsDisplay, ToolsSelector } from './ToolsSelector';
import { IamData } from 'features/iam/services/user-service';
import {
  adjustMentionsForMarkdown,
} from '../../../utils/chat-utilities';
import { MentionsInput, Mention as ReactMention, type OnChangeHandlerFunc, type SuggestionDataItem } from 'react-mentions';
import { useCollaborationSocketStore } from 'state/store/collaboration/use-collaboration-store';
import { useCommonStore } from '../../../state/store/common/useCommonStore';
import logo from '../../../assets/images/bebrave-logo.png';
import { StopGeneratingMessage } from './StopGeneratingMessage';
import { SourceDocumentSelector } from './SourceDocumentSelector';
import { ModelSelector } from '../../../components/blocks/model-selector';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';

const MAX_FILE_SIZE = 20 * 1024 * 1024;

// Typing indicator component with Microsoft Teams-like animation


interface ChatInputProps {
  onSendMessage: (content: string, files: Attachment[], sources?: string[], mentions?: Mention[]) => void;
  isLoading: boolean;
  inputButtons?: ChatInputButton[];
  isCurrentResponseGenerating: boolean;
  onHeightChange?: (height: number) => void;
  isFeatureSelected?: Record<ButtonIds, boolean>;
  hasParticipants?: boolean;
  selectedSources?: string[];
  onSourcesChange?: (sources: string[]) => void;
  onToggleTool?: (toolId: ButtonIds) => void;
  onToggleSourcesTool?: () => void;
  isSourcesToolActive?: boolean;
  isCancelOnGoing?: boolean;
  isModelSelectionDisabled?: boolean;
}

export const ChatInput = ({
  onSendMessage,
  isLoading,
  inputButtons,
  isCurrentResponseGenerating,
  isFeatureSelected,
  onHeightChange,
  hasParticipants,
  selectedSources: externalSelectedSources,
  onSourcesChange: externalOnSourcesChange,
  onToggleTool,
  onToggleSourcesTool: externalOnToggleSourcesTool,
  isSourcesToolActive: externalIsSourcesToolActive,
  isCancelOnGoing,
  isModelSelectionDisabled,
}: ChatInputProps) => {
  const { t } = useTranslation();
  const isInputFocusedRef = useRef<boolean>(false);
  const replyMessage = useChatStore((state) => state.replyMessage);
  const setReplyMessage = useChatStore((state) => state.setReplyMessage);
  // console.log('isCurrentResponseGenerating', isCurrentResponseGenerating)

  const [message, setMessage] = useState('');
  const [messageMarkup, setMessageMarkup] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const mentionsInputRef = useRef<HTMLTextAreaElement | null>(null);
  const [files, setFiles] = useState<Attachment[]>([]);
  const [onProcessing, setOnProcessing] = useState<Attachment[]>([]);
  const [failedFiles, setFailedFiles] = useState<string[]>([]);
  const [emptyFiles, setEmptyFiles] = useState<string[]>([]);
  const selectedModel = useModelStore((state) => state.selectedModel);
  const activeConversationId = useChatStore((state) => state.activeConversationId);
  const isNewChat = useChatStore((state) => state.isNewChat);
  const setNewChat = useChatStore((state) => state.setNewChat);
  const [isSendDisabled, setIsSendDisabled] = useState(true);
  const itemIdFilePathMap = useChatStore((state) => state.itemIdFilePathMap);
  const setItemIdFilePathMap = useChatStore((state) => state.setItemIdFilePathMap);
  const clearItemIdFilePathMap = useChatStore((state) => state.clearItemIdFilePathMap);
  const { toast } = useToast();
  const { configurationSettings } = useConfig();
  const notification = useNotificationStore((state) => state.notification);
  const sendMessage = useCollaborationSocketStore((state: any) => state.sendMessage);
  const [processingToastOpen, setProcessingToastOpen] = useState(false);
  const [processingToastTitle, setProcessingToastTitle] = useState('');
  const [processingToastDescription, setProcessingToastDescription] = useState('');
  const [fileUploadingToastOpen, setFileUploadingToastOpen] = useState(false);
  const [fileUploadingToastTitle, setFileUploadingToastTitle] = useState('');
  const [fileUploadingToastDescription, setFileUploadingToastDescription] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const [isDictationOnGoing, setIsDictationOnGoing] = useState(false);
  const sendTypingStatus = (conversationId: string, isTyping: boolean) => {
    sendMessage({
      type: 'TYPING_STATUS',
      request: { conversationId, is_typing: isTyping },
    });
  }

  const [timeouts, setTimeouts] = useState<Map<string, ReturnType<typeof setTimeout>>>(new Map());


  // Use external state if provided, otherwise use internal state
  const [internalSelectedSources, setInternalSelectedSources] = useState<string[]>([]);
  const selectedSources = externalSelectedSources ?? internalSelectedSources;
  const setSelectedSources = externalOnSourcesChange ?? setInternalSelectedSources;
  console.log('selectedSources in ChatInput:', externalSelectedSources);
  // State to control SourcesSelector popover from ToolsSelector
  const [sourcesPopoverOpen, setSourcesPopoverOpen] = useState(false);
  // State for mobile menu popover
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // State to track if sources tool is active - use external if provided
  const [internalIsSourcesToolActive, setInternalIsSourcesToolActive] = useState(false);
  const isSourcesToolActive = externalIsSourcesToolActive ?? internalIsSourcesToolActive;

  const hasParticipantsArray = useChatStore((state) => state.hasParticipantsArray);
  const [mentions, setMentions] = useState<Mention[]>([]);
  const theme = useTheme();
  const conversations = useChatStore((state) => state.conversations);
  const [isDragging, setIsDragging] = useState(false);

  const isGroupChat = !!hasParticipants && !!hasParticipantsArray && hasParticipantsArray.length > 1;

  const cachedUsers = useMemo(() => {
    const currentConv = activeConversationId ? conversations[activeConversationId] : null;
    const participants = currentConv?.participants ?? [];
    const removedParticipants = currentConv?.removedParticipants ?? [];
    const map = new Map<string, IamData>();
    [...participants, ...removedParticipants].forEach((u) => {
      if (u?.itemId && !map.has(u.itemId)) {
        map.set(u.itemId, u);
      }
    });
    return Array.from(map.values());
  }, [activeConversationId, conversations]);

  const mentionUsers = useMemo(() => {
    // Use cached users (active + removed) and add model as mentionable entity (EagleGpt)
    const currentUserId = useChatStore.getState().activeConversationId
      ? useCommonStore.getState().user?.id
      : useCommonStore.getState().user?.id;
    let users = cachedUsers.filter((u) => u.itemId !== currentUserId);

    if (selectedModel) {
      const modelAsUser: IamData = {
        itemId: selectedModel.id,
        createdDate: '',
        lastUpdatedDate: '',
        lastLoggedInTime: '',
        language: '',
        salutation: '',
        firstName: 'EagleGpt',
        lastName: null,
        email: selectedModel.id,
        userName: selectedModel.id,
        phoneNumber: null,
        roles: [],
        permissions: [],
        active: true,
        isVarified: false,
        profileImageUrl: null,
        mfaEnabled: false,
        organizations: [],
        plan: { itemId: '', name: '' },
        planItem: { itemId: '', title: null },
        groups: [],
      };
      users = [modelAsUser, ...users.filter((u) => u.itemId !== selectedModel.id)];
    }

    // Dedup again (defensive)
    const map = new Map<string, IamData>();
    users.forEach((u) => {
      if (u?.itemId && !map.has(u.itemId)) map.set(u.itemId, u);
    });
    return Array.from(map.values());
  }, [cachedUsers, selectedModel]);



  const mentionData: SuggestionDataItem[] = useMemo(
    () =>
      mentionUsers.map((u) => ({
        id: u.itemId,
        display: `${u.firstName ?? ''}${u.lastName ? ` ${u.lastName}` : ''}`.trim() || u.email || u.itemId,
      })),
    [mentionUsers]
  );

  const mentionDataProvider = useCallback(
    (search: string, callback: (data: SuggestionDataItem[]) => void) => {
      if (!isGroupChat) {
        callback([]);
        return;
      }
      const q = (search || '').toLowerCase();
      if (!q) {
        callback(mentionData);
        return;
      }
      callback(
        mentionData.filter((item) => String(item.display ?? '').toLowerCase().includes(q))
      );
    },
    [isGroupChat, mentionData]
  );

  const mentionUsersMap = useMemo(() => {
    const map = new Map<string, IamData>();
    mentionUsers.forEach((u) => map.set(u.itemId, u));
    return map;
  }, [mentionUsers]);

  const mapReactMentionsToMentions = useCallback(
    (reactMentions: Array<{ id: string; display: string; plainTextIndex: number }>): Mention[] => {
      return reactMentions.map((m) => {
        const display = m.display ?? '';
        const start = m.plainTextIndex;
        const end = start + display.length + 1; // include '@'
        return {
          userId: m.id,
          trigger: `@${display}`,
          start,
          end,
          user: mentionUsersMap.get(m.id),
        };
      });
    },
    [mentionUsersMap]
  );


  useEffect(() => {
    if (isNewChat && !hasParticipants) {
      setMessage('');
      setMessageMarkup('');
      setFiles([]);
      setOnProcessing([]);
      setFailedFiles([]);
      setEmptyFiles([]);
      setIsSendDisabled(true);
      clearAllTimeouts();

      isInputFocusedRef.current = false;
      // setIsTyping(false);
      clearItemIdFilePathMap();
      setProcessingToastOpen(false);
      setNewChat(false);
      setFileUploadingToastOpen(false);
      setReplyMessage(null);
      setSelectedSources([]);
      if (externalIsSourcesToolActive === undefined) {
        setInternalIsSourcesToolActive(false);
      }
      setMentions([]);
    }
  }, [isNewChat, clearItemIdFilePathMap, setNewChat, setReplyMessage, externalIsSourcesToolActive, hasParticipants, activeConversationId, sendTypingStatus]);


  const adjustEditorHeight = useCallback(() => {
    // Use double requestAnimationFrame to ensure DOM is fully rendered
    // This is critical for paste operations where content is added all at once
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (mentionsInputRef.current) {
          // Reset to minimum height first to get accurate scrollHeight when content shrinks
          mentionsInputRef.current.style.height = '48px';
          // Then set to the actual scrollHeight (capped at 160px max)
          const newHeight = Math.max(48, Math.min(mentionsInputRef.current.scrollHeight, 160));
          mentionsInputRef.current.style.height = `${newHeight}px`;
        }
        if (containerRef.current && onHeightChange) {
          const height = containerRef.current.offsetHeight;
          onHeightChange(height + 64);
        }
      });
    });
  }, [onHeightChange]);

  const addTimeout = (fileId: string, timeout: ReturnType<typeof setTimeout>) => {
    setTimeouts((prev) => new Map(prev.set(fileId, timeout)));
  };

  const clearTimeoutByFileId = (fileId: string) => {
    const timeout = timeouts.get(fileId);
    if (timeout) {
      clearTimeout(timeout);
      setTimeouts((prev) => {
        const newMap = new Map(prev);
        newMap.delete(fileId);
        return newMap;
      });
    }
  };

  const clearAllTimeouts = () => {
    timeouts.forEach((timeout) => clearTimeout(timeout));
    setTimeouts(new Map());
  };

  // Typing status functions with debounce
  const handleTypingStop = useCallback(() => {
    if (!activeConversationId) return;

    if (isTyping) {
      sendTypingStatus(activeConversationId, false);
      setIsTyping(false);
    }
  }, [activeConversationId, sendTypingStatus]);


  // react-mentions handles its own dropdown; no outside-click handler needed


  useEffect(() => {
    if (notification && notification.message) {
      // console.log('🔔 NOTIFICATION RECEIVED in ChatInput:', notification);
      const payload = JSON.parse(notification.message['denormalizedPayload']);
      const notificationType = payload['type'];

      try {
        if (notificationType === 'file_upload') {
          const success = payload['success'];
          const message = payload['message'];
          const fileId = payload['file_id'];
          clearTimeoutByFileId(fileId);
          setOnProcessing((prevFiles) => prevFiles.filter((file) => file.fileId !== fileId));

          const fileExists = onProcessing.some((file) => file.fileId === fileId);
          if (!fileExists) {
            return;
          }

          if (!success) {
            setFiles((prevFiles) => prevFiles.filter((file) => file.fileId !== fileId));
            if (message === 'empty_file') {
              setEmptyFiles((prevFiles) => [...prevFiles, fileId]);
            } else {
              setFailedFiles((prevFiles) => [...prevFiles, fileId]);
            }
          }

          if (onProcessing.length > 1) {
            return;
          }

          setProcessingToastOpen(false);

          if (emptyFiles.length > 0 || message === 'empty_file') {
            toast({
              variant: 'destructive',
              title: t('UPLOAD_VECTOR_STORAGE_FAILED'),
              description: t('EMPTY_FILE_DESCRIPTION'),
            });
          } else if (failedFiles.length > 0) {
            toast({
              variant: 'destructive',
              title: t('UPLOAD_VECTOR_STORAGE_FAILED'),
              description: t('NOT_UPLOADED_VECTOR_STORAGE'),
            });
          } else if (success) {
            toast({
              variant: 'success',
              title: t('SUCCESS'),
              description: t('UPLOADED_VECTOR_STORAGE_SUCCESS'),
            });
          } else {
            toast({
              variant: 'destructive',
              title: t('UPLOAD_VECTOR_STORAGE_FAILED'),
              description: t('SOMETHING_WENT_WRONG'),
            });
          }
          setEmptyFiles([]);
          setFailedFiles([]);
        }
      } catch (error) {
        console.error("❌ Error parsing notification payload in ChatInput:", error);
        console.error("❌ Raw notification message:", notification.message);
      }
    } else {
      console.log("🔍 No notification or message found");
    }
  }, [notification]);

  useEffect(() => {
    setFiles([]);
    setReplyMessage(null);
    //setSelectedSources([]);
  }, [activeConversationId, setReplyMessage]);

  // Scroll to bottom and focus input when reply is set
  useEffect(() => {
    if (replyMessage && containerRef.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        // Also scroll window to ensure input is visible
        window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
        // Focus the mentions input
        mentionsInputRef.current?.focus();
      }, 100);
    }
  }, [replyMessage]);

  useEffect(() => {
    adjustEditorHeight();
  }, []);



  useEffect(() => {
    if (message.trim() === '') {
      setIsSendDisabled(true);
    } else {
      setIsSendDisabled(false);
    }
  }, [message]);

  // Clear files when sources are selected
  useEffect(() => {
    const isDocumentManagerActive = selectedSources.includes('document-manager');
    if (selectedSources.length > 0 &&
      files.length > 0 &&
      !isDocumentManagerActive) {
      setFiles([]);
      setOnProcessing([]);
    }
  }, [selectedSources.length, files.length, selectedSources]);

  // Clear sources and deactivate sources tool when Search or Create Image is activated
  useEffect(() => {
    if (isFeatureSelected?.[ButtonIds.SEARCH] || isFeatureSelected?.[ButtonIds.CREATE_IMAGE]) {
      if (selectedSources.length > 0) {
        setSelectedSources([]);
      }
      if (isSourcesToolActive && externalIsSourcesToolActive === undefined) {
        setInternalIsSourcesToolActive(false);
      }
    }
  }, [isFeatureSelected, selectedSources.length, setSelectedSources, isSourcesToolActive, externalIsSourcesToolActive]);

  // Also measure height when files change (affects container height)
  useEffect(() => {
    if (containerRef.current && onHeightChange) {
      const height = containerRef.current.offsetHeight;
      onHeightChange(height + 64);
    }
  }, [files.length, onHeightChange, inputButtons?.length]);

  const handleSubmit = (e: React.FormEvent | KeyboardEvent) => {
    const isGroupChat = hasParticipants && hasParticipantsArray && hasParticipantsArray.length > 1;
    const shouldDisableResponseGenerating = !isGroupChat && isCurrentResponseGenerating;
    if (shouldDisableResponseGenerating) {
      return;
    }
    // const currentMessage =
    if (e && 'preventDefault' in e) {
      e.preventDefault();
    }
    if (message.trim() && !isLoading && onProcessing.length === 0) {

      handleTypingStop();
      let cleanedMessage = message.replace(/^\s+|\s+$/g, '');
      const leadingWhitespaceLength = message.length - message.trimStart().length;
      cleanedMessage = cleanedMessage.replace(/\n/g, '  \n');

      const adjustedMentions =
        mentions.length > 0
          ? adjustMentionsForMarkdown(mentions, message, cleanedMessage, leadingWhitespaceLength)
          : undefined;

      const sources = externalSelectedSources?.includes('document-manager') ? selectedSources.filter((id) => id !== 'document-manager') : selectedSources;
      onSendMessage(cleanedMessage, files, sources, adjustedMentions);
      setMessage('');
      setMessageMarkup('');
      setFiles([]);
      setReplyMessage(null);
      setMentions([]);
      // After clearing the input, ensure the textarea + parent `chatInputHeight` shrink back too.
      // We defer to the next frame so the DOM reflects the cleared value before measuring.
      window.requestAnimationFrame(() => {
        adjustEditorHeight();
      });
    }
  };

  const handlePaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles: File[] = [];
    let hasTextContent = false;

    // Check for image items in clipboard
    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      // Check if the item is an image
      if (item.type.startsWith('image/')) {
        e.preventDefault(); // Prevent default paste behavior for images

        const file = item.getAsFile();
        if (file) {
          imageFiles.push(file);
        }
      } else if (item.type === 'text/plain') {
        hasTextContent = true;
      }
    }

    // If we found image files, check if the model supports images
    if (imageFiles.length > 0) {
      // Check if the selected model allows image uploads (modelType === 1 doesn't support images)
      if (selectedModel?.modelType === 1) {
        toast({
          variant: 'destructive',
          title: t('IMAGE_NOT_SUPPORTED'),
          description: t('IMAGE_NOT_SUPPORTED_DESCRIPTION') || 'The selected model does not support image uploads. Please select a different model.',
        });
        return;
      }

      await onFilesSelected(imageFiles);
    }

    // For text paste, trigger height adjustment after DOM updates with pasted content
    if (hasTextContent) {
      adjustEditorHeight();
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the container itself
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length === 0) return;

    if (!selectedModel) {
      toast({
        variant: 'destructive',
        title: t('ERROR'),
        description: t('NO_MODEL_SELECTED') || 'Please select a model first.',
      });
      return;
    }

    // Get supported extensions for the current model
    const supportedImageExtensions = modelSupportedExtensionsMap[selectedModel.modelType].image;
    const supportedDocumentExtensions = modelSupportedExtensionsMap[selectedModel.modelType].document;
    const allSupportedExtensions = [...supportedImageExtensions, ...supportedDocumentExtensions];

    // Separate valid files, images, unsupported files
    const validImageFiles: File[] = [];
    const validDocumentFiles: File[] = [];
    const unsupportedFiles: string[] = [];

    droppedFiles.forEach((file) => {
      const extension = `.${file.name.split('.').pop()?.toLowerCase()}`;

      if (supportedImageExtensions.includes(extension)) {
        validImageFiles.push(file);
      } else if (supportedDocumentExtensions.includes(extension)) {
        validDocumentFiles.push(file);
      } else {
        unsupportedFiles.push(file.name);
      }
    });

    // Show error for unsupported files
    if (unsupportedFiles.length > 0) {
      toast({
        variant: 'destructive',
        title: t('UNSUPPORTED_FILE_TYPE'),
        description: (
          <div>
            <div>{t('UNSUPPORTED_FILES')}: {unsupportedFiles.join(', ')}.</div>
            <div className="mt-1">{t('SUPPORTED_EXTENSIONS')}: {allSupportedExtensions.join(', ')}</div>
          </div>
        ),
      });
    }

    // Check if model supports images
    if (validImageFiles.length > 0 && selectedModel.modelType === 1) {
      toast({
        variant: 'destructive',
        title: t('IMAGE_NOT_SUPPORTED'),
        description: t('IMAGE_NOT_SUPPORTED_DESCRIPTION') || 'The selected model does not support image uploads. Please select a different model.',
      });

      // Only process document files if any
      if (validDocumentFiles.length > 0) {
        await onFilesSelected(validDocumentFiles);
      }
      return;
    }

    // Process valid files
    const filesToProcess = selectedModel.modelType === 1
      ? validDocumentFiles
      : [...validImageFiles, ...validDocumentFiles];

    if (filesToProcess.length > 0) {
      await onFilesSelected(filesToProcess);
    }
  };



  const iconTypeButtonUi = (inputButton: ChatInputButton) => {
    const handleIconClick = () => {
      if (!inputButton.isSelected) {
        setFiles([]);
      }
      inputButton.onClick();
    };

    const button = (
      <button
        key={inputButton.id}
        aria-pressed="false"
        aria-label="Upload files and more"
        className={
          'flex h-8 min-w-8 items-center justify-center p-2 rounded-full border shadow-sm hover:bg-muted disabled:opacity-30 ' +
          (theme.theme === 'dark' ? 'border-gray-500 opacity-70' : 'border-gray-200') +
          (inputButton.isDisabled ? 'cursor-not-allowed !opacity-30' : '') +
          (inputButton.isSelected ? 'border-primary !text-primary' : '')
        }
        onClick={handleIconClick}
        disabled={inputButton.isDisabled || onProcessing.length > 0}
      >
        {inputButton.icon}
      </button>
    );

    if (inputButton.id == ButtonIds.VOICE) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent>
              <p className="text-xs text-gray-50">{t('COMING_SOON')}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return button;
  };

  const buttonTypeButtonUi = (inputButton: ChatInputButton) => {
    const handleButtonClick = () => {
      if (!inputButton.isSelected) {
        setFiles([]);
      }
      inputButton.onClick();
    };

    const button = (
      <button
        key={inputButton.label}
        className={
          'flex h-8 min-w-8 items-center justify-center p-2 rounded-full border  shadow-sm hover:bg-muted disabled:opacity-30 ' +
          (theme.theme === 'dark' ? 'border-gray-500 opacity-70' : 'border-gray-200') +
          (inputButton.isDisabled ? '!cursor-not-allowed !opacity-30' : '') +
          (inputButton.isSelected ? 'bg-primary !border-primary !text-primary' : '')
        }
        data-testid="composer-button-search"
        aria-pressed="false"
        aria-label="Search"
        onClick={handleButtonClick}
        disabled={inputButton.isDisabled || onProcessing.length > 0}
      >
        {inputButton.icon}
        <span className="flex items-center justify-center">
          <div className="[display:var(--force-hide-label)] ps-1 pe-1 whitespace-nowrap text-[13px]">
            {inputButton.label}
          </div>
        </span>
      </button>
    );

    if (inputButton.id == ButtonIds.VOICE) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent>
              <p className="text-xs text-gray-50">{t('COMING_SOON')}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return button;
  };

  const undoSearchSelection = () => {
    const searchButton = inputButtons?.find((button) => button.id === ButtonIds.SEARCH);
    if (searchButton?.isSelected) {
      searchButton.onClick();
    }
  };

  const fileSizeExceedFilter = (files: File[]) => {
    return files.filter((file) => {
      const parts = file.name.split('.');
      const extension = parts[parts.length - 1];
      if (allSupportedImageExtensions.includes(`.${extension}`) && file.size > MAX_FILE_SIZE) {
        toast({
          variant: 'destructive',
          title: t('UPLOAD_TO_LOCAL_STORAGE_FAILED'),
          description: `${t('MAX_SIZE_EXCEEDED_IMAGE')} (${file.name}) .`,
        });
        return false;
      } else if (file.size > MAX_FILE_SIZE) {
        toast({
          variant: 'destructive',
          title: t('UPLOAD_TO_LOCAL_STORAGE_FAILED'),
          description: `${t('MAX_SIZE_EXCEEDED_DESC')} (${file.name}) .`,
        });
        return false;
      }
      return true;
    });
  };

  const onFilesSelected = async (selectedFiles: File[]) => {
    if (files?.length + selectedFiles?.length > 10) {
      toast({
        title: t('FILE_SELECTION_LIMIT_REACHED') || "File Selection Limit Reached",
        description: t('MAXIMUM_FILE_SELECTED_DESCRIPTION') || "You can only upload up to 10 files at once.",
        variant: "destructive",
      });
      return;
    }

    undoSearchSelection();
    console.log('selected files', selectedFiles);
    selectedFiles = fileSizeExceedFilter(selectedFiles);
    if (selectedFiles.length === 0) {
      return;
    }
    const filesToProcess = selectedFiles.map((file) => {
      const itemId = uuidv4();
      const parts = file.name.split('.');
      const extension = parts[parts.length - 1];
      const attachment: Attachment = {
        fileId: itemId,
        fileName: file.name,
        extension: extension,
        sender: MessageRole.USER,
      };
      if (
        selectedModel &&
        modelSupportedExtensionsMap[selectedModel.modelType].image.includes(`.${extension}`)
      ) {
        setItemIdFilePathMap(itemId, URL.createObjectURL(file));
      }

      return { originalFile: file, attachmentData: attachment };
    });
    const newAttachments = filesToProcess.map((ftp) => ftp.attachmentData);
    setFiles((prevFiles) => [...prevFiles, ...newAttachments]);
    setOnProcessing((prevFiles) => [...prevFiles, ...newAttachments]);

    for (const { originalFile, attachmentData } of filesToProcess) {
      setFileUploadingToastTitle(t('UPLOADING'));
      setFileUploadingToastDescription(` ${attachmentData.fileName} ${t('UPLOADING_FILE')}`);
      setFileUploadingToastOpen(true);

      try {
        const uploadResult = await uploadFileToLocalStorage(
          originalFile,
          attachmentData.fileId,
          configurationSettings
        );

        if (uploadResult['isSuccess'] || !uploadResult['isSuccess']) {
          setFileUploadingToastOpen(false);
        }

        if (!uploadResult['isSuccess']) {
          setFiles((prevFiles) =>
            prevFiles.filter((file) => file.fileId !== attachmentData.fileId)
          );
          console.error(uploadResult);
          toast({
            variant: 'destructive',
            title: t('UPLOAD_TO_LOCAL_STORAGE_FAILED'),
            description: `${attachmentData.fileName} ${t('NOT_UPLOADED_LOCAL_STORAGE')}.`,
          });
        } else {
          console.log(`${attachmentData.fileName} is uploaded to local storage successfully.`);
          if (
            !VECTORIZE_EXCLUDE_EXTENSIONS.includes(attachmentData.extension) &&
            selectedModel && (
              modelSupportedExtensionsMap[selectedModel.modelType].document.includes(
                `.${attachmentData.extension}`
              ) || modelSupportedExtensionsMap[selectedModel.modelType].image.includes(
                `.${attachmentData.extension}`
              ))
          ) {
            setProcessingToastTitle(t('PROCESSING'));
            setProcessingToastDescription(
              `${attachmentData.fileName} ${t('PROCESSING_FILE_VECTOR')}`
            );
            setProcessingToastOpen(true);

            const vectorUploadItems: VectorUploadItem[] = [{ fileId: attachmentData.fileId }];
            try {
              await uploadFilesToVectorStorage(
                vectorUploadItems,
                selectedModel.id,
                configurationSettings
              );
              const timeoutId = setTimeout(() => {
                setProcessingToastOpen(false);

                setOnProcessing((prevFiles) =>
                  prevFiles.filter((file) => file.fileId !== attachmentData.fileId)
                );

                toast({
                  variant: 'warning',
                  title: t('SUCCESS'),
                  description: `${attachmentData.fileName} ${t('UPLOADED_VECTOR_STORAGE_SUCCESS_TIMEOUT')}.`,
                });
              }, 300000);
              addTimeout(attachmentData.fileId, timeoutId);
            } catch (err) {
              setProcessingToastOpen(false);

              setFiles((prevFiles) =>
                prevFiles.filter((file) => file.fileId !== attachmentData.fileId)
              );
              console.error(`vector upload failed with ${err}`);
              toast({
                variant: 'destructive',
                title: t('UPLOAD_VECTOR_STORAGE_FAILED'),
                description: t('NOT_UPLOADED_VECTOR_STORAGE'),
              });
            }
          } else {
            setOnProcessing((prevFiles) =>
              prevFiles.filter((file) => file.fileId !== attachmentData.fileId)
            );
          }
        }
      } catch (err: any) {
        setFileUploadingToastOpen(false);
        setFiles((prevFiles) => prevFiles.filter((file) => file.fileId !== attachmentData.fileId));
        setOnProcessing((prevFiles) =>
          prevFiles.filter((file) => file.fileId !== attachmentData.fileId)
        );
        toast({
          variant: 'destructive',
          title: t('UPLOAD_TO_LOCAL_STORAGE_FAILED'),
          description:
            err?.status == 403
              ? t('YOU_DONT_HAVE_ENOUGH_PERMISSION_TO_UPLOAD_FILE')
              : `${attachmentData.fileName} ${t('NOT_UPLOADED_LOCAL_STORAGE')}.`,
        });
      }
    }
  };

  const removeFile = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const mentionsStyle = useMemo(() => {
    return {
      control: {
        minHeight: 48,
        maxHeight: 160,
        fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '22px',
        backgroundColor: 'transparent',
        overflow: 'hidden',
      },
      highlighter: {
        padding: '0.25rem 0.75rem',
        border: 0,
        outline: 'none',
        color: 'transparent',
        fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '22px',
        maxHeight: 160,
        overflow: 'hidden',
      },
      input: {

        border: 0,
        outline: 'none',
        color: 'hsl(var(--foreground))',
        fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '22px',
        maxHeight: 160,
        overflow: 'auto',
      },
      mention: {
        backgroundColor: 'hsl(var(--primary-50))',
        color: 'hsl(var(--primary-500))',
        fontWeight: 700,
        borderRadius: 6,
        padding: '0 2px',
      },
      suggestions: {
        list: {
          backgroundColor: 'hsl(var(--background))',
          border: '1px solid hsl(var(--border))',
          fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '22px',
          // show max 2 items, rest scrollable
          maxHeight: 120,
          overflowY: 'auto',
          width: 320,
          borderRadius: 8,
          boxShadow: '0 10px 15px -3px hsl(var(--foreground) / 0.08), 0 4px 6px -4px hsl(var(--foreground) / 0.08)',
        },
        item: {
          padding: 0,
        },
        placeholder: {

          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '22px',
        },

      },
    } as const;
  }, [theme.theme]);

  const handleMentionsChange: OnChangeHandlerFunc = (_e, newValue, newPlainTextValue, reactMentions) => {
    setMessageMarkup(newValue);
    setMessage(newPlainTextValue);

    // Parse mentions detected by react-mentions
    const parsedMentions = isGroupChat
      ? mapReactMentionsToMentions(reactMentions as Array<{ id: string; display: string; plainTextIndex: number }>)
      : [];

    // Also scan for @mentions in plain text that weren't detected by react-mentions
    // (e.g., from pasted text that wasn't selected via the dropdown)
    if (isGroupChat && newPlainTextValue) {
      // Sort users by name length (descending) to match longer names first
      const sortedUsers = [...mentionUsers].sort((a, b) => {
        const nameA = `${a.firstName ?? ''}${a.lastName ? ` ${a.lastName}` : ''}`.trim();
        const nameB = `${b.firstName ?? ''}${b.lastName ? ` ${b.lastName}` : ''}`.trim();
        return nameB.length - nameA.length;
      });

      // Track positions already covered by existing mentions
      const coveredRanges = parsedMentions.map(m => ({ start: m.start, end: m.end }));

      for (const user of sortedUsers) {
        const fullName = `${user.firstName ?? ''}${user.lastName ? ` ${user.lastName}` : ''}`.trim();
        if (!fullName) continue;

        // Escape special regex characters in name
        const escapedName = fullName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Match @Name (case-insensitive) that's not followed by alphanumeric or ]
        const pattern = new RegExp(`@${escapedName}(?![\\]\\w])`, 'gi');

        let match;
        while ((match = pattern.exec(newPlainTextValue)) !== null) {
          const start = match.index;
          const end = start + match[0].length;

          // Check if this position is already covered by an existing mention
          const isAlreadyCovered = coveredRanges.some(
            range => (start >= range.start && start < range.end) || (end > range.start && end <= range.end)
          );

          if (!isAlreadyCovered) {
            // Add this as an additional mention
            parsedMentions.push({
              userId: user.itemId,
              trigger: `@${fullName}`,
              start,
              end,
              user: mentionUsersMap.get(user.itemId),
            });

            // Mark this range as covered
            coveredRanges.push({ start, end });
          }
        }
      }

      // Sort mentions by start position
      parsedMentions.sort((a, b) => a.start - b.start);
    }

    setMentions(parsedMentions);
    setIsSendDisabled(newPlainTextValue.trim().length === 0);

    if (newPlainTextValue.length > 0 && activeConversationId && isInputFocusedRef.current) {
      if (!isTyping) {
        sendTypingStatus(activeConversationId, true);
        setIsTyping(true);
      }
    }

    // Height adjust
    adjustEditorHeight();
  };

  const isSelectDocumentChipShow = selectedSources?.find(source => source === 'document-manager') ? true : false;

  const handleSelectDocument = (docs: any[]) => {
    const selectedDoc = docs.map(doc => ({
      fileId: doc.id,
      fileName: doc.name,
      extension: doc.metadata?.extension || '',
      sender: MessageRole.USER,
    }));

    setFiles(prevFiles => {
      const existingIds = new Set(prevFiles.map(f => f.fileId));

      const uniqueNewFiles = selectedDoc.filter(
        doc => !existingIds.has(doc.fileId)
      );

      return [...prevFiles, ...uniqueNewFiles];
    });
  };


  console.log('selectedDoc', files);

  return (
    <>
      <CustomToast
        open={processingToastOpen}
        onOpenChange={setProcessingToastOpen}
        title={processingToastTitle}
        description={processingToastDescription}
        variant="warning"
        preventAutoClose={true}
      />

      <CustomToast
        open={fileUploadingToastOpen}
        onOpenChange={setFileUploadingToastOpen}
        title={fileUploadingToastTitle}
        description={fileUploadingToastDescription}
        variant="warning"
        preventAutoClose={true}
      />
      <div
        ref={containerRef}
        className={`w-full  relative rounded-2xl border-[0.5px] border-primary-600 ${theme.theme === 'dark' ? 'bg-black-100' : 'bg-white'} shadow-md p-4 sm:p-5 ${isDragging ? 'border-primary border-2 bg-primary/5' : ''
          }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      // onClick={connect}
      >
        {/* Drag and Drop Overlay */}
        {isDragging && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-primary/10 backdrop-blur-sm rounded-2xl border-2 border-dashed border-primary">
            <div className="text-center">
              <div className="text-primary font-semibold text-lg mb-2">
                {t('DROP_FILES_HERE') || 'Drop files here'}
              </div>
              <div className="text-sm text-muted-foreground">
                {selectedModel?.modelType === 1
                  ? t('DOCUMENTS_ONLY') || 'Documents only'
                  : t('IMAGES_AND_DOCUMENTS') || 'Images and documents supported'}
              </div>
            </div>
          </div>
        )}
        {replyMessage && (
          <div className={`mb-3 pb-3 border-b ${theme.theme === 'dark' ? 'border-gray-600' : 'border-gray-200'}`}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2">
                <span className={`text-sm font-medium ${theme.theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                  {replyMessage.repliedUser
                    ? `${replyMessage.repliedUser.firstName}${replyMessage.repliedUser.lastName ? ` ${replyMessage.repliedUser.lastName}` : ''}`
                    : 'User'}
                </span>
                {replyMessage.generation.createdDate && (
                  <span className={`text-xs ${theme.theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {formatDate(new Date(replyMessage.generation.createdDate))}
                  </span>
                )}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setReplyMessage(null);
                }}
                className="flex-shrink-0 p-1 rounded-full hover:bg-muted transition-colors"
                aria-label="Cancel reply"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
            <div className={`text-sm ${theme.theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} truncate`}>
              {replyMessage.generation.content || 'Message with attachments'}
            </div>
          </div>
        )}

        {/* Selected Sources Display - Now handled by ToolsSelector */}

        {files.length > 0 && (
          <div className="flex flex-wrap justify-start items-start gap-4 mb-4">
            {files.map((file, index) => {
              if (itemIdFilePathMap.has(file.fileId)) {
                return (
                  <div key={index} className="relative bg-transparent p-2 rounded">
                    <img
                      className="w-16 h-16 object-cover"
                      src={itemIdFilePathMap.get(file.fileId)}
                      alt={file.fileName}
                    />
                    {onProcessing.some((pFile) => pFile.fileId === file.fileId) ? (
                      <Loader2 className="absolute top-7 right-7 h-6 w-6 animate-spin text-primary" />
                    ) : (
                      <button
                        onClick={() => removeFile(index)}
                        className="absolute top-2 right-2 bg-white rounded-full text-primary hover:text-primary-700"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                );
              }

              return (
                <div
                  key={index}
                  className={`flex items-center justify-between p-2 gap-2 rounded ${theme.theme === 'dark' ? 'bg-[#27272c]' : 'bg-primary-light'}`}
                >
                  <div className="flex items-center gap-2">
                    {/* <FileText className="h-4 w-4 text-gray-500" /> */}
                    <div className='w-5 h-5 flex items-center justify-center rounded bg-[#8B5CF6]'>
                      <FileStack className="h-4 w-4 text-white" />
                    </div>
                    <span className={`text-sm truncate max-w-[180px] ${theme.theme === 'dark' ? 'text-primary-200' : 'text-neutral-800'}`}>
                      {file.fileName}
                    </span>
                  </div>
                  {onProcessing.some((pFile) => pFile.fileId === file.fileId) ? (
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  ) : (
                    <button
                      onClick={() => removeFile(index)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!isDictationOnGoing && (
          <form className="mx-auto flex items-center gap-4" onSubmit={handleSubmit}>
            <div className="relative w-full">
              <MentionsInput
                value={messageMarkup}
                onPaste={handlePaste}
                onChange={handleMentionsChange}
                inputRef={(el: HTMLTextAreaElement | null) => {
                  mentionsInputRef.current = el;
                }}
                placeholder={t('CHAT_INPUT_PLACEHOLDER')}
                disabled={!hasParticipants && isLoading && !isCurrentResponseGenerating}
                forceSuggestionsAboveCursor={true}
                style={mentionsStyle as any}
                className="w-full"
                onKeyDown={(e) => {
                  // When suggestions are open, react-mentions will intercept Enter to select.
                  // This handler is only called when suggestions are NOT open.
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                onFocus={() => {
                  isInputFocusedRef.current = true;
                }}
                onBlur={() => {
                  isInputFocusedRef.current = false;
                  if (isTyping && activeConversationId) {
                    handleTypingStop();
                  }
                }}
              >
                {isGroupChat ? (
                  <ReactMention
                    trigger="@"
                    data={mentionDataProvider}
                    appendSpaceOnAdd={true}
                    markup="@__[__display__](__id__)"
                    // Ensure the rendered text includes the '@' prefix (single @)
                    displayTransform={(_id: string, display: string) => `@${display}`}
                    renderSuggestion={(
                      suggestion: SuggestionDataItem,
                      _search: string,
                      highlightedDisplay: React.ReactNode,
                      _index: number,
                      focused: boolean
                    ) => {
                      const user = mentionUsersMap.get(String(suggestion.id));
                      const displayName =
                        user
                          ? `${user.firstName ?? ''}${user.lastName ? ` ${user.lastName}` : ''}`.trim()
                          : String(suggestion.display ?? suggestion.id);
                      const email = user?.email ?? '';
                      const avatarSrc = user?.profileImageUrl || logo;

                      return (
                        <div
                          className={[
                            'flex items-center gap-2 p-2 rounded-lg cursor-pointer w-full text-left transition-all border border-transparent',
                            focused ? 'bg-muted border-border' : 'hover:bg-muted hover:border-border',
                          ].join(' ')}
                        >
                          <div className="flex-shrink-0">
                            <img
                              src={avatarSrc}
                              alt={displayName || 'user'}
                              className="w-6 h-6 rounded-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-foreground truncate">
                              {highlightedDisplay || displayName}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {email}
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                ) : (
                  <ReactMention trigger="@" data={[]} />
                )}
              </MentionsInput>
            </div>
          </form>
        )}
        {inputButtons && inputButtons.length > 0 && (
          <div className="flex flex-row justify-between gap-2">
            {!isDictationOnGoing && <div className="flex justify-start mt-2 gap-2 xl:gap-4 items-center">
              <Popover open={mobileMenuOpen} onOpenChange={setMobileMenuOpen} modal={false}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`h-[34px] w-[34px] rounded-full border-[0.5px] xl:hidden flex items-center justify-center ${theme.theme === 'dark' ? 'border-primary-200' : 'border-primary-600'}`}
                  >

                    <PlusIcon className={`h-5 w-5 ${theme.theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`} />
                    <span className="sr-only">{t('MORE_OPTIONS') || 'More options'}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-48 p-0 z-50"
                  align="start"
                  side="top"
                  sideOffset={10}
                  onInteractOutside={(e) => {
                    // Prevent closing when clicking on nested popovers
                    const target = e.target as HTMLElement;
                    if (target.closest('[data-radix-popper-content-wrapper]') ||
                      target.closest('[data-sources-selector]')) {
                      e.preventDefault();
                    }
                  }}
                >
                  <div className="flex flex-col p-1">
                    {/* File Upload */}
                    <div
                      onPointerDown={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      className=""
                    >
                      <div onClick={(e) => e.stopPropagation()}>
                        <FileUploadPopover
                          onFilesSelected={(files) => {
                            onFilesSelected?.(files);
                            setMobileMenuOpen(false);
                          }}
                          isFeatureSelected={isFeatureSelected}
                          disabled={selectedSources.length > 0}
                        />
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="h-px bg-border -mx-1 my-2" />

                    {/* Tools Selector */}
                    <div
                      onPointerDown={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      className=""
                    >
                      <div
                        onPointerDown={(e) => {
                          // Allow sources button clicks to work
                          const target = e.target as HTMLElement;
                          if (target.closest('button[aria-label="Select sources"]') ||
                            target.closest('[data-sources-button]') ||
                            target.closest('[data-radix-popper-content-wrapper]')) {
                            return; // Don't stop propagation for sources button
                          }
                          e.stopPropagation();
                        }}
                        onMouseDown={(e) => {
                          const target = e.target as HTMLElement;
                          if (target.closest('button[aria-label="Select sources"]') ||
                            target.closest('[data-sources-button]') ||
                            target.closest('[data-radix-popper-content-wrapper]')) {
                            return;
                          }
                          e.stopPropagation();
                        }}
                        onClick={(e) => {
                          const target = e.target as HTMLElement;
                          if (target.closest('button[aria-label="Select sources"]') ||
                            target.closest('[data-sources-button]') ||
                            target.closest('[data-radix-popper-content-wrapper]')) {
                            return;
                          }
                          e.stopPropagation();
                        }}
                      >
                        <ToolsSelector
                          isFeatureSelected={isFeatureSelected}
                          onToggleTool={(toolId: ButtonIds) => {
                            onToggleTool?.(toolId);
                            setMobileMenuOpen(false);
                          }}
                          disabled={false}
                          onOpenSourcesSelector={() => {
                            setSourcesPopoverOpen(true);
                            // Don't close mobile menu - let SourcesSelector open and handle closing
                          }}
                          selectedSources={selectedSources}
                          isSourcesToolActive={isSourcesToolActive}
                          onToggleSourcesTool={() => {
                            if (externalIsSourcesToolActive !== undefined) {
                              externalOnToggleSourcesTool?.();
                            } else {
                              setInternalIsSourcesToolActive(!internalIsSourcesToolActive);
                              externalOnToggleSourcesTool?.();
                            }
                            setMobileMenuOpen(false);
                          }}
                          onSourcesChange={setSelectedSources}
                          sourcesPopoverOpen={sourcesPopoverOpen}
                          onSourcesPopoverOpenChange={(open) => {
                            setSourcesPopoverOpen(open);
                            if (open) {
                              // Close mobile menu when sources popover opens, but with delay
                              setTimeout(() => setMobileMenuOpen(false), 150);
                            }
                          }}
                        />
                      </div>
                    </div>

                    {/* Source Document Selector */}
                    {/* {isSelectDocumentChipShow && (
                      <>
                        <div className="h-px bg-border -mx-1 " />
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center justify-start"
                        >
                          <SourceDocumentSelector onSelect={handleSelectDocument} />
                        </div>
                      </>
                    )} */}
                  </div>
                </PopoverContent>
              </Popover>
              <ModelSelector isDisabled={isModelSelectionDisabled} />

              <ActiveToolsDisplay
                className="xl:hidden flex items-center gap-2 ml-0 xl:ml-2"
                isFeatureSelected={isFeatureSelected}
                onToggleTool={(toolId: ButtonIds) => onToggleTool?.(toolId)}
                onOpenSourcesSelector={() => setSourcesPopoverOpen(true)}
                selectedSources={selectedSources}
                isSourcesToolActive={isSourcesToolActive}
                onToggleSourcesTool={() => {
                  if (externalIsSourcesToolActive !== undefined) {
                    externalOnToggleSourcesTool?.();
                  } else {
                    setInternalIsSourcesToolActive(!internalIsSourcesToolActive);
                    externalOnToggleSourcesTool?.();
                  }
                }}
                onSourcesChange={setSelectedSources}
                sourcesPopoverOpen={sourcesPopoverOpen}
                onSourcesPopoverOpenChange={setSourcesPopoverOpen}
              />
              <div className={`w-[0.5px] h-6 ${theme.theme === 'dark' ? 'bg-primary-200' : 'bg-primary'} xl:block hidden`} />
              <div className="xl:flex hidden items-center gap-4">
                <FileUploadPopover
                  onFilesSelected={onFilesSelected}
                  isFeatureSelected={isFeatureSelected}
                  disabled={selectedSources.length > 0}
                />
                <ToolsSelector
                  isFeatureSelected={isFeatureSelected}
                  onToggleTool={(toolId: ButtonIds) => onToggleTool?.(toolId)}
                  disabled={false}
                  onOpenSourcesSelector={() => setSourcesPopoverOpen(true)}
                  selectedSources={selectedSources}
                  isSourcesToolActive={isSourcesToolActive}
                  onToggleSourcesTool={() => {
                    if (externalIsSourcesToolActive !== undefined) {
                      // Use external handler if provided
                      externalOnToggleSourcesTool?.();
                    } else {
                      // Use internal state
                      setInternalIsSourcesToolActive(!internalIsSourcesToolActive);
                      externalOnToggleSourcesTool?.();
                    }
                  }}
                  onSourcesChange={setSelectedSources}
                  sourcesPopoverOpen={sourcesPopoverOpen}
                  onSourcesPopoverOpenChange={setSourcesPopoverOpen}
                />
              </div>
              {isSelectDocumentChipShow && <SourceDocumentSelector onSelect={handleSelectDocument} />}


            </div>}
            <div className={
              "flex mt-2 gap-2 xl:gap-4 items-center" +
              (isDictationOnGoing ? " w-full" : " justify-start")
            }>
              {inputButtons
                .filter((button) => button.alignment === Alignment.RIGHT)
                .map((inputButton) =>
                  inputButton.type === ButtonTypes.BUTTON
                    ? buttonTypeButtonUi(inputButton)
                    : iconTypeButtonUi(inputButton)
                )}
              <DictateButton
                isDictationOnGoing={isDictationOnGoing}
                setIsDictationOnGoing={setIsDictationOnGoing}
                onTranscribe={(newMessage) => {
                  console.log('newMessage onTranscribe', newMessage)
                  // Append dictation text into MentionsInput (preserves existing mention markup)
                  setMessageMarkup((prev) => (prev ? `${prev} ${newMessage}` : newMessage));
                  setMessage((prev) => (prev ? `${prev} ${newMessage}` : newMessage));
                  setIsSendDisabled(false);
                  setTimeout(() => mentionsInputRef.current?.focus(), 0);
                  adjustEditorHeight();
                }}
              />
              {!isDictationOnGoing && (() => {
                // For group chat: allow sending if current user is the sender of the streaming response
                // For normal chat: disable when response is generating
                const isGroupChat = hasParticipants && hasParticipantsArray && hasParticipantsArray.length > 1;
                // const isCurrentUserSender = streamingMessage?.response?.message?.senderId === currentUser?.id;


                const shouldDisableResponseGenerating = !isGroupChat && isCurrentResponseGenerating;

                const isDisabled = isSendDisabled ||
                  onProcessing.length > 0 ||
                  shouldDisableResponseGenerating;

                if (shouldDisableResponseGenerating) {
                  return (
                    <StopGeneratingMessage
                      isLoading={isLoading}
                      isCancelOnGoing={isCancelOnGoing}
                    />);
                }
                return (
                  <Button
                    variant="outline"
                    disabled={isDisabled}
                    aria-disabled="false"
                    aria-label="Send message"
                    className={`disabled:opacity-40 flex items-center bg-transparent justify-center h-[34px] hover:bg-muted rounded-full border-[0.5px] w-[34px] ${theme.theme === 'dark' ? 'border-primary-200' : 'border-primary-600'}`}
                    onClick={handleSubmit}
                  >
                    <Send size={16} className={`${theme.theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`} />
                  </Button>
                );
              })()}
            </div>
          </div>
        )}
        {hasParticipants && (
          <TypingIndicator />
        )}

      </div>

    </>
  );
};
